import React from 'react';
// import { Route } from 'react-router';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage, searchPlugin } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { CatalogListFilters } from './components/catalog/CatalogIndexPageCustom';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import { AlertDisplay, OAuthRequestDialog, SignInPage } from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { configApiRef, githubAuthApiRef, microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { ExplorePage } from '@backstage-community/plugin-explore';
import { CostInsightsPage } from '@backstage-community/plugin-cost-insights';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { DfpPage } from '@internal/plugin-dfp';
import { Route } from 'react-router-dom';
import { AiDevToolsPage } from '@internal/plugin-ai-dev-tools';
import { AiPlatformPage } from '@internal/plugin-ai-platform';
import { TechDocsFeedbackAddon } from '@internal/plugin-custom-entity-feedback';
import { customSearchUiPlugin } from '@internal/plugin-custom-search-ui';
import { CompanyTechRadarPage } from './components/tech-radar/CompanyTechRadarPage';
import { appThemes } from './theme/themes';

const app = createApp({
  apis,
  themes: appThemes,
  components: {
    SignInPage: props => {
      const configApi = useApi(configApiRef);
      if (configApi.getString('auth.environment') === 'development') {
        return (
          <SignInPage
            {...props}
            auto
            providers={[
              'guest',
              {
                id: 'microsoft-auth-provider',
                title: 'AzureAD',
                message: 'Sign in using AzureAD',
                apiRef: microsoftAuthApiRef,
              },
              {
                id: 'github-auth-provider',
                title: 'GitHub',
                message: 'Sign in using GitHub',
                apiRef: githubAuthApiRef,
              }
            ]}
          />
        )
      }
      return (
        <SignInPage
          {...props}
          auto
          provider={{
            id: 'microsoft-auth-provider',
            title: 'AzureAD',
            message: 'Sign in using AzureAD',
            apiRef: microsoftAuthApiRef,
          }}
        />
      )
    }
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
    // bind(explorePlugin.externalRoutes, {
    //   catalogEntity: catalogPlugin.routes.catalogEntity,
    // });
    bind(customSearchUiPlugin.externalRoutes, {
      search: searchPlugin.routes.root,
    });
  },
});

const routes = (
  <FlatRoutes>
    {/* <Navigate key="/" to="catalog" /> */}
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage filters={<CatalogListFilters />} />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
        <TechDocsFeedbackAddon />
        <Mermaid config={{ theme: 'default', themeVariables: { lineColor: '#000000' } }} />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/tech-radar" element={<CompanyTechRadarPage />} />
    <Route path="/explore" element={<ExplorePage />} />
    <Route path="/cost-insights" element={<CostInsightsPage />} />
    <Route path="/dfp" element={<DfpPage />} />
    <Route path="/ai-dev-tools" element={<AiDevToolsPage />} />
    <Route path="/ai-platform/*" element={<AiPlatformPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>
);
